import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deliveriesApi, type Delivery } from "../api/deliveries";
import { card, head, list, itemLink, moreLink, empty } from "./widgetStyles";

/**
 * The lab's queue, and nothing else. Chosen by `deliveries:quality`.
 *
 * <p>No money appears here, on purpose: the backend already masks settlement
 * figures for quality-only callers, and the dashboard does not go looking for
 * a way around that.
 */
export function QualityQueueWidget({ limit = 6 }: { limit?: number }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<Delivery[] | null>(null);

  useEffect(() => {
    void deliveriesApi
      // Red laboratorije: strana je veća od onoga što se crta, jer se posle čitanja zadržavaju samo
      // isporuke bez ijednog nalaza. Ranije je ovde putovao ceo registar primljenih pa se sekao ovde.
      // A receipt imported from the ERP takes no finding in the portal, so it never waits for the lab.
      .list({ status: "RECEIVED", origin: "PORTAL" }, 0, 100)
      .then((r) => setItems(r.content.filter((d) => d.qualityValues.length === 0).slice(0, limit)))
      .catch(() => setItems([]));
  }, [limit]);

  if (items === null) return null;

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("widgets.qualityQueue.title", "Waiting for analysis")}</strong>
        <Link to="/procurement/deliveries" className="widget-more-link" style={moreLink}>
          {t("widgets.qualityQueue.viewAll", "All deliveries")}
        </Link>
      </header>
      {items.length === 0 ? (
        <p style={empty}>{t("widgets.qualityQueue.empty", "Nothing is waiting for the lab.")}</p>
      ) : (
        <ul style={list}>
          {items.map((d) => (
            <li key={d.id}>
              <Link to={`/procurement/deliveries/${d.id}`} style={itemLink}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <code style={{ fontSize: 12, color: "var(--brand-ink)" }}>
                    {d.weighbridgeTicketNo ?? d.vehiclePlate ?? "—"}
                  </code>
                  <small style={{ color: "var(--ink-3)" }}>
                    {/* Uvezen prijem nema najavu; pokazuje se dan prijema, a prazno ostaje prazno. */}
                    {(d.receivedOn ?? d.announcedDate)
                      ? new Date((d.receivedOn ?? d.announcedDate) as string).toLocaleDateString(i18n.resolvedLanguage)
                      : "—"}
                  </small>
                </div>
                <small style={{ display: "block", color: "var(--ink-3)", marginTop: 2 }}>
                  {d.companyName} · {d.netKg ?? "—"} kg
                </small>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
